import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Admin } from '../types/auth';
import { userService } from '../services/api';

interface AuthContextType {
  user: Admin | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (data: { currentPassword: string; newPassword: string }) => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to validate token and get user data
  const validateToken = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {

      setIsLoading(false);
      return;
    }

    try {

      const response = await userService.getProfile();

      if (response && response.success && response.data) {
        const userData: Admin = {
          id: response.data.id,
          name: response.data.name,
          email: response.data.email,
          role: 'admin',
          status: response.data.status,
          image: response.data.image,
          emailVerifiedAt: response.data.emailVerifiedAt,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
          deletedAt: response.data.deletedAt
        };
        setUser(userData);
      } else {
        console.error('Profile response was not successful:', response);
        throw new Error(response?.message || 'Failed to get profile data');
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate token on mount
  useEffect(() => {
    validateToken();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);

      const response = await userService.login({ email, password });

      if (response.success) {

        const userData: Admin = {
          id: response.data.adminData.id,
          name: response.data.adminData.name,
          email: response.data.adminData.email,
          role: 'admin',
          status: response.data.adminData.status,
          image: response.data.adminData.image,
          emailVerifiedAt: response.data.adminData.emailVerifiedAt,
          createdAt: response.data.adminData.createdAt,
          updatedAt: response.data.adminData.updatedAt,
          deletedAt: response.data.adminData.deletedAt
        };

        // Store tokens exactly as received from backend
        const accessToken = response.data.tokens.accessToken;
        const refreshToken = response.data.tokens.refreshToken;

        // Store tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        setUser(userData);

      } else {

        throw new Error(response.message);
      }
    } catch (error) {

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const changePassword = async (data: { currentPassword: string; newPassword: string }) => {
    try {
      const response = await userService.changePassword(data);
      // Check if the API response indicates success
      if (!response.success) {
        throw new Error(response.message || 'Password change failed');
      }
      return response;
    } catch (error) {
      console.error('Password change failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, changePassword, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
